<template>
    <div>
        <div class="row mb-2">
            <div class="col-6 align-self-center">
                <span v-if="showHeader" class="related-docs-header">Related documents</span>
            </div>
            <div class="col-6 text-right align-self-center">
                <button @click="addDocument" :disabled="disabled" class="btn btn-sm btn-primary">
                    + Add Document
                </button>
            </div>
        </div>

        <span v-if="isRelatedDocsFetching" class="text-muted">
            Loading...
        </span>
        <span v-else-if="docsList.length === 0" class="text-muted">
            No data
        </span>
        <template v-else>
            <template v-for="groupKey in sorterNonEmptyGroupKeys">
                <h5 v-if="groupKey !== 'Other' || (groupKey === 'Other' && groupedDocs[groupKey].length !== docsList.length)"
                    class="mt-3">
                    {{ groupKey }}
                </h5>

                <ag-grid-vue
                    :style="`height: ${tableHeight(groupKey)}px;`"
                    style="width: 100%; height: 500px;"
                    class="ag-theme-balham"
                    :columnDefs="columnDefs"
                    :rowData="groupedDocs[groupKey]"
                    :defaultColDef="defaultColDef"/>

            </template>
        </template>

        <SearchDocuments name="add-related-docs">
            <template v-slot:footer-left>
                <AppDropdown
                    id="doc-type-list"
                    :data="documentTypesVisible"
                    @select="onUploadNewDoc"
                    align="right"
                    :search="{ show: true, property: 'name', caseSensitive: false }">
                    <template v-slot:menu-button>
                        <div class="btn btn-link btn-sm mt-0">
                            Upload and connect new document
                        </div>
                    </template>
                    <template #item="{ item }">
                        {{ item.name }}
                    </template>
                </AppDropdown>
            </template>
        </SearchDocuments>
    </div>
</template>

<script>
import axios from 'axios'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import {DOCS_BACK_STACK_PUSH} from '@/store/modules/documents'
import {DOC_FETCH_RELATED_DOCUMENTS} from '@/store/modules/doc-viewer'
import AgGridCellRendererContainer from './ag-grid/renderers/AgGridCellRendererContainer'
import AgGridCellRendererActions from './ag-grid/renderers/components/AgGridCellRendererActions'
import {openDocumentViewer} from '../index'
import {convertDocumentSectionForDocumentsAPI} from '@/types/document/types'
import {getNotInStatusesFilter, getUserFilter} from '@/views/documents/types/views/search'
import {AppDocSystemStatuses} from '@/types/document/status'
import {showErrorMessage, showSuccessMessage} from '@/services/snackbar'
import SearchDocuments from '@/views/documents/single/search/SearchDocuments'
import {INFO} from '@/store/modules/ui/UI'
import {
    getRelatedDocColumnLabel,
    RelatedDocColumnKey
} from '@/views/documents/subtypes/settings/editor/section/related-docs'

export default {
    name: 'RelatedDocuments',
    // eslint-disable-next-line vue/no-unused-components
    components: {SearchDocuments, AgGridCellRendererContainer, AgGridCellRendererActions},
    props: {
        item: {
            type: Object,
            required: false // for document subtype configuration modal it can be missed
        },
        showHeader: {
            type: Boolean,
            default: true
        },
        showViewAssetsAction: {
            type: Boolean,
            default: false
        },
        selectedUuid: { // related doc uuid
        },
        settings: {
            type: Object, // RelatedDocsFormSectionParams
            default: () => ({})
        }
    },
    data() {
        return {
            isRelatedDocsFetching: false,
            deletingDocUuid: null,

            columnDefs: [],
            defaultColDef: null
        }
    },
    computed: {
        ...mapGetters([
            'userUUID'
        ]),

        ...mapGetters('documents', [
            'documentTypes'
        ]),

        ...mapGetters('doc_type_permissions', [
            'checkIsVisibleDocTypeForCurrentUser',
            'hasOwnOnlyAccess'
        ]),

        ...mapGetters('doc_viewer', {
            doc: 'doc',
            docsList: 'relatedDocsList',
            groups: 'relatedDocsGroups'
        }),

        documentTypesVisible() {
            return this.documentTypes.filter(dt => this.checkIsVisibleDocTypeForCurrentUser(dt))
        },

        sorterGroupKeys() {
            return Object.keys(this.groups).filter(group => !!String(group)).sort(a => a === 'Other' ? 1 : -1)
        },

        sorterNonEmptyGroupKeys() {
            return this.sorterGroupKeys.filter(groupKey => this.groupedDocs[groupKey] && this.groupedDocs[groupKey].length !== 0)
        },

        groupedDocs() {
            const obj = {}
            this.docsList.forEach(doc => {
                const groupKey = Object.keys(this.groups).find(groupKey => _.isArray(this.groups[groupKey]) && this.groups[groupKey].some(uuid => uuid === doc.uuid))
                if (groupKey) {
                    if (!obj[groupKey]) {
                        obj[groupKey] = []
                    }
                    obj[groupKey].push(doc)
                } else {
                    if (!obj['Other']) {
                        obj['Other'] = []
                    }
                    obj['Other'].push(doc)
                }
            })
            return obj
        },

        disabled() {
            return !this.item || !this.item.uuid
        },

        hasCustomSettings() {
            return this.settings && this.settings.columns && this.settings.columns.length > 0
        }
    },
    methods: {
        ...mapMutations('documents', {
            docsBackStackPush: DOCS_BACK_STACK_PUSH
        }),

        ...mapActions('doc_viewer', {
            fetchRelatedDocuments: DOC_FETCH_RELATED_DOCUMENTS
        }),

        openDocument(doc) {
            this.docsBackStackPush(this.item)
            openDocumentViewer({id: doc.uuid})
        },

        addDocument() {
            const excludeCurrentDocFilter = {bool: {must_not: [{terms: {'uuid.keyword': [this.doc.uuid]}}]}}
            const filter = [
                getNotInStatusesFilter([
                    AppDocSystemStatuses.SCANNING,
                    AppDocSystemStatuses.FLAGGED_HIDDEN
                ]),
                excludeCurrentDocFilter
            ]
            if (this.hasOwnOnlyAccess) {
                filter.push(getUserFilter(this.userUUID))
            }

            this.$modal.show('add-related-docs', {
                esFilter: filter,
                onSelect: this.connectDocuments,
                multiple: true
            })
        },

        async connectDocuments(docs) {
            if (docs.length === 0) {
                return
            }
            const promises = []
            for (const childDoc of docs) {
                promises.push(axios.post('documents/relates', {
                    fromType: convertDocumentSectionForDocumentsAPI(this.doc.section),
                    fromUUID: this.doc.uuid,
                    toType: convertDocumentSectionForDocumentsAPI(childDoc.section),
                    toUUID: childDoc.uuid
                }))
            }
            try {
                await axios.all(promises)
                showSuccessMessage('Documents connected')
                await this.fetchRelatedDocs()
            } catch (e) {
                showErrorMessage('Failed to connect documents')
            }
        },

        async fetchRelatedDocs() {
            if (this.disabled) {
                return
            }

            this.isRelatedDocsFetching = true
            await this.fetchRelatedDocuments(this.item)
            this.isRelatedDocsFetching = false
        },

        tableHeight(groupKey) {
            if (this.groupedDocs[groupKey].length < 5) {
                return 192
            } else if (this.groupedDocs[groupKey].length > 10) {
                return 340
            } else {
                return (this.groupedDocs[groupKey].length + 2) * 34
            }
        },

        onUploadNewDoc(documentType) {
            this.$bus.$emit('DOCUMENT_ADD', {
                documentType,
                onSuccessUpload: doc => this.connectDocuments([doc])
            })
        },

        checkIsVisibleColumn(columnKey) {
            if (!this.hasCustomSettings) {
                return true
            }
            const col = this.settings.columns.find(col => col.key === columnKey)
            return col ? col.visible : true
        },

        getColumnWidth(columnKey) {
            if ([
                RelatedDocColumnKey.NUMBER,
                RelatedDocColumnKey.USERS,
                RelatedDocColumnKey.DATE,
                RelatedDocColumnKey.TOTAL
            ].includes(columnKey)) {
                return 90
            }
            return 120
        },

        setColumnDefs() {
            this.defaultColDef = {
                width: 120,
                sortable: true,
                filter: true,
                editable: false,
                resizable: true,
                autoHeight: true,
                cellRendererFramework: AgGridCellRendererContainer
            }

            const columnsDefs = Object.values(RelatedDocColumnKey).map(key => {
                return {
                    field: key,
                    headerName: getRelatedDocColumnLabel(key),
                    hide: !this.checkIsVisibleColumn(key),
                    width: this.getColumnWidth(key)
                }
            })

            columnsDefs.push({
                field: 'actions',
                headerName: '',
                sortable: false,
                cellRendererFramework: AgGridCellRendererActions,
                cellRendererParams: {
                    showViewAssetsAction: this.showViewAssetsAction
                },
                width: 130
            })

            this.columnDefs = columnsDefs
        }
    },
    mounted() {
        this.setColumnDefs()
        this.fetchRelatedDocs()

        this.$bus.$on('DELETED_RELATED_DOCUMENT', () => {
            this.fetchRelatedDocs()
        })

        this.$bus.$on('DOCUMENT_CREATED', doc => {
            this.connectDocuments([doc]).then(() => {
                this.$snackbar.show({type: INFO, text: 'Related document added'})
                this.fetchRelatedDocs()
            })
        })
    },
    destroyed() {
        this.$bus.$off('DELETED_RELATED_DOCUMENT')
        this.$bus.$off('DOCUMENT_CREATED')
    },
    watch: {
        'item.uuid'() {
            this.fetchRelatedDocs()
        },
        settings: {
            handler() {
                this.setColumnDefs()
            },
            deep: true
        }
    }
}
</script>

<style scoped lang="scss">

.related-docs-header {
    font-size: 18px;
    font-weight: 600;
    color: $color-black-soft;
}

</style>
